/**
 * Project Benefits Panel Component
 * 
 * Shows benefits linked to a specific project with:
 * - Expected Value, Current Value, Realized Value
 * - Attainment % with trend
 * - Status breakdown
 * 
 * Questions Answered:
 * - What value should this project create?
 * - What value has it created?
 * - Are benefits improving or degrading?
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Target, AlertTriangle, CheckCircle, TrendingUp, TrendingDown,
  DollarSign, Plus, RefreshCw, Loader2, Calendar, Users,
  ArrowUpRight, ArrowDownRight, Activity, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

const formatCurrency = (value, currency = 'USD') => {
  if (value === undefined || value === null) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `${currency === 'GBP' ? '£' : '$'}${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${currency === 'GBP' ? '£' : '$'}${(value / 1000).toFixed(0)}K`;
  }
  return `${currency === 'GBP' ? '£' : '$'}${value.toLocaleString()}`;
};

const formatPercent = (value) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(1)}%`;
};

const AttainmentGauge = ({ value, size = 'medium' }) => {
  const percentage = Math.min(value || 0, 120);
  const sizeMap = {
    small: { container: 'w-12 h-12', text: 'text-xs' },
    medium: { container: 'w-16 h-16', text: 'text-sm' },
    large: { container: 'w-24 h-24', text: 'text-xl' }
  };
  const s = sizeMap[size] || sizeMap.medium;
  
  const getColor = (val) => {
    if (val >= 100) return '#10b981';
    if (val >= 80) return '#10b981';
    if (val >= 60) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className={`relative ${s.container}`}>
      <svg className="transform -rotate-90" viewBox="0 0 36 36">
        <path
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="3"
        />
        <path
          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
          fill="none"
          stroke={getColor(value)}
          strokeWidth="3"
          strokeDasharray={`${Math.min(percentage, 100)}, 100`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`${s.text} font-bold`} style={{ color: getColor(value) }}>
          {(value || 0).toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusConfig = {
    on_track: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'On Track' },
    exceeded: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Exceeded' },
    at_risk: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'At Risk' },
    below_target: { bg: 'bg-red-100', text: 'text-red-700', label: 'Below Target' },
    cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Cancelled' },
    deferred: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Deferred' }
  };
  
  const config = statusConfig[status] || statusConfig.on_track;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const LifecycleBadge = ({ lifecycle }) => {
  const lifecycleConfig = {
    proposed: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Proposed' },
    approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
    tracked: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Tracked' },
    realized: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Realized' },
    closed: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Closed' }
  };
  
  const config = lifecycleConfig[lifecycle] || lifecycleConfig.proposed;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const CategoryIcon = ({ category }) => {
  const iconMap = {
    financial: DollarSign,
    operational: Activity,
    strategic: Target,
    customer: Users,
    productivity: TrendingUp,
    compliance: CheckCircle,
    risk_reduction: AlertTriangle
  };
  const Icon = iconMap[category] || Target;
  return <Icon className="w-4 h-4" />;
};

/**
 * ProjectBenefitsPanel - Standalone component for project benefits
 * 
 * @param {string} projectId - The project ID to fetch benefits for
 * @param {string} className - Optional additional CSS classes
 * @param {function} onBenefitClick - Callback when a benefit is clicked
 */
const ProjectBenefitsPanel = ({ projectId, className = '', onBenefitClick }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchBenefits = useCallback(async (showRefreshing = false) => {
    if (!projectId) return;
    
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${API_BASE}/benefits/by-project/${projectId}`,
        { headers }
      );
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch project benefits:', err);
      setError('Failed to load benefits');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchBenefits();
  }, [fetchBenefits]);

  const handleRefresh = () => fetchBenefits(true);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="project-benefits-loading">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl border p-6 ${className}`} data-testid="project-benefits-error">
        <div className="text-center py-8 text-slate-500">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-400" />
          <p>{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-slate-100 rounded-lg text-sm hover:bg-slate-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const benefits = data?.benefits || [];
  const summary = {
    total_expected: data?.total_expected_value || 0,
    total_realized: data?.total_realized_value || 0,
    attainment: data?.attainment_percent || 0,
    count: data?.benefits_count || benefits.length,
    on_track: data?.benefits_on_track || benefits.filter(b => b.status === 'on_track' || b.status === 'exceeded').length,
    at_risk: data?.benefits_at_risk || benefits.filter(b => b.status === 'at_risk' || b.status === 'below_target').length
  };

  const valueGap = summary.total_expected - summary.total_realized;

  return (
    <div className={`bg-white rounded-xl border ${className}`} data-testid="project-benefits-panel">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-500" />
              Project Benefits
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Expected value from this project investment
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="refresh-benefits-btn"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="p-6 bg-gradient-to-r from-slate-50 to-slate-100/50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Expected Value</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_expected)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Realized Value</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{formatCurrency(summary.total_realized)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Value Gap</p>
            <p className={`text-xl font-bold mt-1 ${valueGap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {valueGap > 0 ? '-' : '+'}{formatCurrency(Math.abs(valueGap))}
            </p>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Attainment</p>
            <AttainmentGauge value={summary.attainment} size="small" />
          </div>
        </div>
        
        {/* Status Summary */}
        <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-sm text-slate-600">{summary.on_track} On Track</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-sm text-slate-600">{summary.at_risk} At Risk</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-slate-300"></div>
            <span className="text-sm text-slate-600">{summary.count} Total</span>
          </div>
        </div>
      </div>

      {/* Benefits List */}
      <div className="p-6">
        {benefits.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium text-slate-600">No benefits linked to this project</p>
            <p className="text-sm mt-1">Benefits define the expected value from project investments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {benefits.map((benefit) => (
              <div
                key={benefit.benefit_id}
                className={`p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer ${
                  benefit.status === 'at_risk' || benefit.status === 'below_target'
                    ? 'border-l-4 border-l-amber-500 bg-amber-50/30'
                    : 'border-l-4 border-l-emerald-500 bg-slate-50/50'
                }`}
                onClick={() => onBenefitClick?.(benefit)}
                data-testid={`benefit-${benefit.benefit_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="p-1 rounded bg-slate-100">
                        <CategoryIcon category={benefit.category} />
                      </span>
                      <StatusBadge status={benefit.status} />
                      <LifecycleBadge lifecycle={benefit.lifecycle} />
                    </div>
                    <h4 className="font-medium text-slate-900 truncate">{benefit.title}</h4>
                    {benefit.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{benefit.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Expected</p>
                      <p className="font-semibold text-slate-700">{formatCurrency(benefit.expected_value)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Realized</p>
                      <p className="font-semibold text-slate-700">{formatCurrency(benefit.realized_value)}</p>
                    </div>
                    <AttainmentGauge value={benefit.attainment_percent || 0} size="small" />
                  </div>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    {benefit.owner_name && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {benefit.owner_name}
                      </span>
                    )}
                    {benefit.target_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(benefit.target_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectBenefitsPanel;
export { ProjectBenefitsPanel };
